import { useEffect, useState } from "react";
import { X, CalendarDays, Ruler, ScrollText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/lib/api";
import Reveal from "@/components/Reveal";

const GROUPS = [
  { key: "portero", label: "Porteros", singular: "Portero" },
  { key: "defensa", label: "Defensas", singular: "Defensa" },
  { key: "centrocampista", label: "Centrocampistas", singular: "Centrocampista" },
  { key: "delantero", label: "Delanteros", singular: "Delantero" },
];

const singularOf = (pos) => GROUPS.find((g) => g.key === pos)?.singular || pos;

const PlayerCard = ({ player, index, onOpen }) => (
  <Reveal delay={Math.min(index * 0.04, 0.3)}>
    <button
      onClick={() => onOpen(player)}
      data-testid={`player-card-${player.number}`}
      className="group relative w-full overflow-hidden rounded-2xl border border-edge bg-ink2 p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:border-crimson/60"
    >
      <span className="pointer-events-none absolute -right-2 -top-5 font-display text-8xl font-black text-white/[0.04]">
        {player.number}
      </span>
      <p className="font-mono-score text-3xl font-bold text-crimson">
        {String(player.number).padStart(2, "0")}
      </p>
      <p className="mt-3 font-display text-xl font-extrabold uppercase leading-tight text-white">
        {player.name}
      </p>
      <p className="mt-1 font-mono-score text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {singularOf(player.position)}
      </p>
      <p className="mt-3 font-mono-score text-[9px] uppercase tracking-[0.25em] text-slate-600 transition-colors group-hover:text-glow">
        Ver ficha →
      </p>
      <div className="stripes-rojiblancas absolute bottom-0 left-0 h-1 w-0 opacity-80 transition-all duration-500 group-hover:w-full" />
    </button>
  </Reveal>
);

const PlayerModal = ({ player, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <AnimatePresence>
      {player && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/85 p-4 backdrop-blur-sm"
          onClick={onClose}
          data-testid="player-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md overflow-hidden rounded-3xl border border-edge bg-ink2 p-8"
          >
            <div className="stripes-rojiblancas absolute inset-x-0 top-0 h-1.5 opacity-80" />
            <span className="pointer-events-none absolute -right-4 -top-8 font-display text-[10rem] font-black leading-none text-white/[0.05]">
              {player.number}
            </span>
            <button
              onClick={onClose}
              data-testid="player-modal-close"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-edge text-slate-400 transition-colors hover:border-crimson hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>

            <p className="font-mono-score text-5xl font-bold text-crimson">
              {String(player.number).padStart(2, "0")}
            </p>
            <h2 className="mt-3 font-display text-3xl font-black uppercase leading-tight text-white">
              {player.name}
            </h2>
            <p className="mt-1 inline-block rounded-full bg-crimson/15 px-3 py-1 font-mono-score text-[11px] font-bold uppercase tracking-[0.25em] text-glow">
              {singularOf(player.position)}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-edge bg-ink p-4" data-testid="player-age">
                <p className="flex items-center gap-2 font-mono-score text-[10px] uppercase tracking-widest text-slate-500">
                  <CalendarDays className="h-3.5 w-3.5 text-crimson" /> Edad
                </p>
                <p className="mt-1 font-display text-2xl font-extrabold text-white">
                  {player.age ? `${player.age} años` : "—"}
                </p>
              </div>
              <div className="rounded-xl border border-edge bg-ink p-4" data-testid="player-height">
                <p className="flex items-center gap-2 font-mono-score text-[10px] uppercase tracking-widest text-slate-500">
                  <Ruler className="h-3.5 w-3.5 text-crimson" /> Altura
                </p>
                <p className="mt-1 font-display text-2xl font-extrabold text-white">
                  {player.height || "—"}
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-edge bg-ink p-4" data-testid="player-career">
              <p className="flex items-center gap-2 font-mono-score text-[10px] uppercase tracking-widest text-slate-500">
                <ScrollText className="h-3.5 w-3.5 text-crimson" /> Trayectoria
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-300">
                {player.career || "Ficha por completar desde el panel (pestaña Plantilla)."}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Plantilla = () => {
  const [players, setPlayers] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    API.get("/players").then(({ data }) => setPlayers(data)).catch(() => setPlayers([]));
  }, []);

  return (
    <div className="bg-ink pt-32" data-testid="plantilla-page">
      <div className="mx-auto max-w-7xl px-5 pb-24 md:px-12">
        <Reveal>
          <p className="eyebrow mb-3">Temporada 2026/27</p>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
            Plantilla <span className="text-crimson">rojiblanca</span>
          </h1>
          <p className="mt-4 max-w-2xl text-slate-300">
            Los guerreros que defienden el escudo esta temporada. Pulsa cualquier ficha para ver su edad,
            altura y trayectoria.
          </p>
        </Reveal>

        {players === null ? (
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-36 animate-pulse rounded-2xl border border-edge bg-ink2" />
            ))}
          </div>
        ) : (
          GROUPS.map((g) => {
            const group = players.filter((p) => p.position === g.key);
            if (!group.length) return null;
            return (
              <section key={g.key} className="mt-14" data-testid={`squad-group-${g.key}`}>
                <Reveal>
                  <div className="mb-5 flex items-center gap-4">
                    <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
                      {g.label}
                    </h2>
                    <span className="rounded-full bg-crimson/15 px-3 py-1 font-mono-score text-[11px] font-bold text-glow">
                      {group.length}
                    </span>
                    <div className="h-px flex-1 bg-edge" />
                  </div>
                </Reveal>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {group.map((p, i) => (
                    <PlayerCard key={p.number} player={p} index={i} onOpen={setSelected} />
                  ))}
                </div>
              </section>
            );
          })
        )}

        {players !== null && players.length === 0 && (
          <p className="mt-16 text-center text-slate-500">
            La plantilla se está preparando. Vuelve en breve.
          </p>
        )}
      </div>

      <PlayerModal player={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default Plantilla;
